/** 네비게이션 로직 훅 — IXD/DSD 포커싱 훅을 조합하는 orchestrator */

import { useCallback } from "react";
import { IxdChunk, MatchedIxdCell } from "@/apis/compare/route";
import { MatchNavigation } from "@/app/ixd-dsd-compare/types";
import { IxdPreviewRendererRef } from "@/components/ixd-preview";
import { makeCellIdentityKey } from "@/utils/ixd-cell-model";
import { useIxdFocus } from "./use-ixd-focus";

interface UseNavigationParams {
  ixdRendererRef?: React.RefObject<IxdPreviewRendererRef>;
  matchNavigation: MatchNavigation | null;
  onMatchNavigationChange: (nav: MatchNavigation | null) => void;
  /** 현재 렌더링 중인 IXD chunk 목록 (청크 경계 감지용) */
  currentIxdChunks?: IxdChunk[];
  /** 포커스된 셀의 IXD chunk가 변경될 때 호출 */
  onActiveIxdChunkChange?: (chunk: IxdChunk) => void;
  /** 현재 활성 IXD chunk ID (같은 chunk 로의 중복 통지를 거르는 데 쓴다) */
  activeIxdChunkId?: string;
}

export const useNavigation = ({
  ixdRendererRef,
  matchNavigation,
  onMatchNavigationChange,
  currentIxdChunks,
  onActiveIxdChunkChange,
  activeIxdChunkId,
}: UseNavigationParams) => {
  const ixdFocus = useIxdFocus({
    ixdRendererRef,
    currentIxdChunks,
    onActiveIxdChunkChange,
    activeIxdChunkId,
  });

  // --- IXD match navigation ---

  const handleNavigatePrev = useCallback(() => {
    if (!matchNavigation || matchNavigation.currentIndex === 0) return;
    const newIndex = matchNavigation.currentIndex - 1;
    onMatchNavigationChange({ ...matchNavigation, currentIndex: newIndex });
    ixdFocus.focusByIndex(matchNavigation.matchingCells, newIndex);
  }, [matchNavigation, onMatchNavigationChange, ixdFocus]);

  const handleNavigateNext = useCallback(() => {
    if (
      !matchNavigation ||
      matchNavigation.currentIndex === matchNavigation.matchingCells.length - 1
    )
      return;
    const newIndex = matchNavigation.currentIndex + 1;
    onMatchNavigationChange({ ...matchNavigation, currentIndex: newIndex });
    ixdFocus.focusByIndex(matchNavigation.matchingCells, newIndex);
  }, [matchNavigation, onMatchNavigationChange, ixdFocus]);

  const handleNavigateToIndex = useCallback(
    (index: number) => {
      if (!matchNavigation) return;
      if (index < 0 || index >= matchNavigation.matchingCells.length) return;
      if (index === matchNavigation.currentIndex) return;
      onMatchNavigationChange({ ...matchNavigation, currentIndex: index });
      ixdFocus.focusByIndex(matchNavigation.matchingCells, index);
    },
    [matchNavigation, onMatchNavigationChange, ixdFocus],
  );

  const handleNavigationClose = useCallback(() => {
    ixdFocus.clearFocus();
    onMatchNavigationChange(null);
  }, [ixdFocus, onMatchNavigationChange]);

  const handleNavigationDismiss = useCallback(() => {
    onMatchNavigationChange(null);
  }, [onMatchNavigationChange]);

  // --- Matched value click (DSD → IXD cross-reference) ---

  const handleMatchedValueClick = useCallback(
    (
      value: string,
      matchedIxdCells: MatchedIxdCell[],
      mostProperCellId?: string | null,
      dlViewId?: string | null,
      period?: string | null,
    ) => {
      const cellIds = matchedIxdCells.map((c) => c.cellId);
      // cellId 는 축만 다른 물리 셀끼리 충돌할 수 있다. cellId 별로 기대 identity
      // 집합을 만들어 필터한다. metadata 없는 ref(구버전 레코드, 블록/문장 매칭)가
      // 섞인 cellId 는 어떤 물리 셀이 매칭인지 단정할 수 없으므로 그 cellId 만
      // 필터를 끈다 — 나머지 cellId 의 축 충돌 필터는 유지된다.
      const identityKeysByCellId = new Map<string, string[]>();
      const unfilterableCellIds = new Set<string>();
      for (const c of matchedIxdCells) {
        if (!c.metadata) {
          unfilterableCellIds.add(c.cellId);
          continue;
        }
        const keys = identityKeysByCellId.get(c.cellId) ?? [];
        keys.push(makeCellIdentityKey(c.metadata));
        identityKeysByCellId.set(c.cellId, keys);
      }
      unfilterableCellIds.forEach((cellId) =>
        identityKeysByCellId.delete(cellId),
      );
      if (cellIds.length > 0) {
        const nav = ixdFocus.focusByCellIds({
          cellIds,
          identityKeysByCellId:
            identityKeysByCellId.size > 0 ? identityKeysByCellId : undefined,
          value,
          alwaysReturnNav: undefined,
          mostProperCellId,
          dlViewId,
          period,
        });
        // 단수차 매칭은 팝오버에 차이값 배지를 표기한다.
        const differenceByCellId = new Map<string, number>();
        for (const c of matchedIxdCells) {
          if (c.displayDifference != null) {
            differenceByCellId.set(c.cellId, c.displayDifference);
          }
        }
        onMatchNavigationChange(
          nav && differenceByCellId.size > 0
            ? { ...nav, differenceByCellId }
            : nav,
        );
      }
    },
    [ixdFocus, onMatchNavigationChange],
  );

  // --- Inferred cell focusing (DSD 추론값 → IXD) ---

  /**
   * 추론값으로 IXD 를 포커싱한다. 매칭 클릭과 달리 **배정된 DL 밖으로 나가지 않는다**
   * — 추론값은 그 DL 안의 제안이라, 같은 cell_id 를 쓰는 다른 DL 의 값으로 데려가면
   * 그 값이 이 DSD 셀의 대응인 것처럼 보인다. 대응 셀이 없으면 그 DL 까지만 안내한다.
   */
  const focusInferredCell = useCallback(
    ({
      value,
      inferredCellId,
      dlViewId,
      period,
      bgColor,
    }: {
      value: string;
      inferredCellId: string | null;
      dlViewId?: string | null;
      period?: string | null;
      bgColor?: string;
    }) => {
      if (!inferredCellId) {
        ixdFocus.clearFocus();
        onMatchNavigationChange({ value, matchingCells: [], currentIndex: 0 });
        return;
      }
      // alwaysReturnNav: 1개 셀도 navigation 반환
      const nav = ixdFocus.focusByCellIds({
        cellIds: [inferredCellId],
        value,
        dlViewId,
        period,
        restrictToDlView: true,
        alwaysReturnNav: true,
        bgColor,
      });
      onMatchNavigationChange(
        nav ?? { value, matchingCells: [], currentIndex: 0 },
      );
    },
    [ixdFocus, onMatchNavigationChange],
  );

  const handleShouldTagCellClick = useCallback(
    (
      value: string,
      inferredCellId: string | null,
      dlViewId?: string | null,
      period?: string | null,
    ) =>
      focusInferredCell({
        value,
        inferredCellId,
        dlViewId,
        period,
        bgColor: "#FEE2E2", // DSD should-tag 셀과 동일한 red-100
      }),
    [focusInferredCell],
  );

  /** 우클릭한 데이터 셀의 추론값 포커싱 — should-tag 와 같은 제약을 받는다. */
  const handleInferredCellFocus = useCallback(
    (
      inferredCellId: string,
      value: string,
      dlViewId?: string | null,
      period?: string | null,
    ) => focusInferredCell({ value, inferredCellId, dlViewId, period }),
    [focusInferredCell],
  );

  // --- Header cell click (DSD 헤더 → 대응 IXD 헤더 포커싱) ---

  const handleHeaderFocus = useCallback(
    (tag: {
      lineItem?: string | null;
      period?: string | null;
      member?: string | null;
    }) => {
      const focused = ixdFocus.focusHeaderByTag(tag);
      // 헤더 포커싱은 값 매칭이 아니므로 match-navigation 패널을 닫는다.
      if (focused) onMatchNavigationChange(null);
      return focused;
    },
    [ixdFocus, onMatchNavigationChange],
  );

  return {
    matchNavigation,
    handleNavigatePrev,
    handleNavigateNext,
    handleNavigateToIndex,
    handleNavigationClose,
    handleNavigationDismiss,
    handleMatchedValueClick,
    handleShouldTagCellClick,
    handleInferredCellFocus,
    handleHeaderFocus,
    // IXD 셀 직접 클릭 시 CSS 강조 + 팝오버를 함께 닫아야 하므로 handleNavigationClose를 사용한다
    clearIxdFocus: handleNavigationClose,
  };
};
